import React from 'react';
import { DefaultButton, Label, PrimaryButton, Separator, Spinner, SearchBox, Dropdown, IDropdownOption } from "@fluentui/react";
import { Add24Regular } from '@fluentui/react-icons';
import { connect } from "react-redux";
import { useBoolean } from '@fluentui/react-hooks';
import { ErrorMessageComponent } from "../../../Error/ErrorMessageComponent";
import { useEffect, useMemo, useRef, useState } from "react";
import { ContractConcept } from "../../../../models/Entities/Contracts/ContractConcept";
import ConceptFormComponent from "../ConceptForm/ConceptForm.Component";
import { ConceptItemComponent } from "./ConceptItem.Component";
import { useToastController } from '@fluentui/react-components';
import { AppToast, toastTimeout } from '../../../../commons/component/AppToast/AppToast.Component';

// functions
import * as ComponentFunctions from './ConceptList.Functions';

////i18Next
import { useTranslation } from 'react-i18next';

// styles
import './ConceptList.Styles.css';

export const ConceptListComponent = (props: ComponentFunctions.Props) => {

    // initial values
    const [isOpenInitialSettings, { setTrue: openPanelInitialSettings, setFalse: dismissPanelInitialSettings }] = useBoolean(false);
    const [selectedConcept, setSelectedConcept] = useState<ContractConcept | undefined>(undefined);
    const [filterText, setFilterText] = useState<string>("");
    const [selectedBranchIds, setSelectedBranchIds] = useState<number[]>([]);
    const [cloneInProgress, setCloneInProgress] = useState<boolean>(false);
    const [cloningConceptId, setCloningConceptId] = useState<number | null>(null);
    // Concepto cuya categoría se está guardando: el spinner va sólo en esa tarjeta.
    const [categoryConceptId, setCategoryConceptId] = useState<number | null>(null);
    const cloneLoadingStartedRef = useRef(false);

    // Add or eddit Concept
    const [isOpenVisit, { setTrue: openPanelConcept, setFalse: dismissPanelConcept }] = useBoolean(false);

    const handleInitialSettingsClosePanel = () => {
        dismissPanelInitialSettings();
    };

    const handleConceptClosePanel = () => {
        setSelectedConcept(undefined);
        dismissPanelConcept();
    };

    const handleChargeVisitInformation = () => {
        openPanelInitialSettings();
    };

    const HandleAddEvent = () => {
        openPanelConcept();
    };

    const HandleEditEvent = (concept: ContractConcept) => {
        setSelectedConcept(concept);
        openPanelConcept();
    };

    const HandleDeleteEvent = (concept: ContractConcept) => {
        props.DeleteContractConcept(concept.id, props.ContractId, props.ContractAddendumId);
    };

    const HandleCloneEvent = (concept: ContractConcept) => {
        cloneLoadingStartedRef.current = false;
        setCloneInProgress(true);
        setCloningConceptId(concept.id);
        props.CloneContractConcept(props.ContractId, props.ContractAddendumId, concept.id);
    };

    //useTransaltion()
    const [t] = useTranslation();
    const { dispatchToast } = useToastController('PrebillerToast');

    useEffect(() => {
        if (props.isUpdatingConcept || categoryConceptId === null) return;
        if (props.failOnUpdatingConcept) {
            dispatchToast(
                <AppToast intent="error" title={t('conceptList.toast.categoryError', 'No se pudo cambiar la categoría')} body={props.updateConceptError?.ErrorMessage} items={props.updateConceptError?.Errors} />,
                { intent: 'error', timeout: toastTimeout('error') }
            );
            props.ResetContractConceptSaveFlags();
        } else {
            // Se relee del servidor: así la tarjeta muestra lo que quedó guardado, no lo que se mandó.
            props.GetAllContractsConceptByAddendum(props.ContractId, props.ContractAddendumId);
        }
        setCategoryConceptId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isUpdatingConcept]);

    const resolveBranchId = (branch: any): number | undefined => {
        const rawId = branch?.branchId ?? branch?.id;
        const parsedId = Number(rawId);
        return Number.isFinite(parsedId) ? parsedId : undefined;
    };

    const getConceptBranchIds = (concept: ContractConcept): number[] => {
        const ids = new Set<number>();

        (concept.branches || []).forEach((branch) => {
            const id = resolveBranchId(branch);
            if (id !== undefined) {
                ids.add(id);
            }
        });

        const directCandidates = [
            (concept as any)?.branchId,
            (concept as any)?.branchid,
            (concept as any)?.concept?.branchId,
            (concept as any)?.concept?.branchid,
            (concept as any)?.concept?.branch?.id,
            (concept as any)?.concept?.branch?.branchId,
        ];

        directCandidates.forEach((raw) => {
            const parsed = Number(raw);
            if (Number.isFinite(parsed)) {
                ids.add(parsed);
            }
        });

        return Array.from(ids);
    };

    // efects
    useEffect(() => {
        if (props.Contract) {
            props.GetAllContractsConceptByAddendum(props.ContractId, props.ContractAddendumId);
        }
    }, [props.Contract === undefined, props.ContractAddendumId]);

    useEffect(() => {
        if (!cloneInProgress) {
            return;
        }

        if (props.isLoading) {
            cloneLoadingStartedRef.current = true;
            return;
        }

        if (!cloneLoadingStartedRef.current) {
            return;
        }

        cloneLoadingStartedRef.current = false;

        if (props.failOnLoading) {
            dispatchToast(
                <AppToast intent="error" title={t('conceptList.toast.cloneError', 'No se pudo clonar el concepto')} body={props.error?.ErrorMessage} items={props.error?.Errors} />,
                { intent: 'error', timeout: toastTimeout('error') }
            );
        } else {
            dispatchToast(
                <AppToast intent="success" title={t('conceptList.toast.cloneSuccess', 'Concepto clonado correctamente')} />,
                { intent: 'success', timeout: toastTimeout('success') }
            );
        }

        setCloneInProgress(false);
        setCloningConceptId(null);
    }, [cloneInProgress, props.isLoading, props.failOnLoading, dispatchToast, t]);

    const branchOptions: IDropdownOption[] = useMemo(() => {
        const namesById = new Map<number, string>();

        (props.Addendum?.branches || []).forEach((branch) => {
            namesById.set(branch.id, (branch.branchName || '').trim() || `#${branch.id}`);
        });

        (props.ContractConcepts || []).forEach((concept) => {
            (concept.branches || []).forEach((branch) => {
                const id = resolveBranchId(branch);
                if (id === undefined) {
                    return;
                }
                const name = (branch.branchName || '').trim() || namesById.get(id) || `#${id}`;
                if (!namesById.has(id)) {
                    namesById.set(id, name);
                }
            });

            getConceptBranchIds(concept).forEach((id) => {
                if (!namesById.has(id)) {
                    namesById.set(id, `#${id}`);
                }
            });
        });

        return Array.from(namesById.entries())
            .map(([id, name]) => ({ key: id, text: name }))
            .sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' }));
    }, [props.Addendum?.branches, props.ContractConcepts]);

    const handleBranchFilterChange = (
        _event: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption
    ) => {
        if (!option) {
            return;
        }

        const optionId = Number(option.key);
        setSelectedBranchIds((current) =>
            option.selected
                ? [...current, optionId]
                : current.filter((id) => id !== optionId)
        );
    };

    // Loading
    const Loading = props.isLoading && (
        <div>
            <Spinner
                label={t('conceptList.loading.loadingConcepts')}
                ariaLive="assertive"
                labelPosition="top"
            />
        </div>
    );

    const ComponentActions = (
        <div className="ConceptListActions">
            <PrimaryButton
                secondaryText={t('conceptList.componentActions.addConcept')}
                onClick={HandleAddEvent}
                text={t('conceptList.componentActions.addConcept')}
                onRenderIcon={() => <Add24Regular />}
                className="actionButton"
            />
            <SearchBox
                className="conceptSearch"
                placeholder={t('conceptList.search.placeholder', 'Buscar conceptos por nombre, referencia o descripción')}
                value={filterText}
                onChange={(_, newValue) => setFilterText(newValue || "")}
                onClear={() => setFilterText("")}
                styles={{ root: { maxWidth: 380, margin: '8px 0' } }}
            />
            <Dropdown
                className="conceptBranchFilter"
                placeholder={t('conceptList.filters.branchesPlaceholder', 'Filtrar por rama')}
                selectedKeys={selectedBranchIds}
                options={branchOptions}
                onChange={handleBranchFilterChange}
                multiSelect
                disabled={branchOptions.length === 0}
                styles={{ dropdown: { minWidth: 250, margin: '8px 0' } }}
            />
        </div>
    );

    const ErrorMessage = props.failOnLoading && props.error && (
        <ErrorMessageComponent Error={props.error} />
    );

    const NoRegistries = (props.isLoadingSuccessfully && props.ContractConcepts?.length === 0 || props.ContractConcepts?.length === undefined) && (
        <div className="NoDataSection">
            <img
                src={require('../../../../assets/img/empty_state.gif')}
                alt="Ichtys"
                style={{ width: '10em', height: '10em', marginBottom: '1em' }}
            ></img>
            <Label>{t('conceptList.noRegistries.noConceptsRegistered.noConceptsRegistered')}</Label>
        </div>
    );

    const Forms = <>
        <ConceptFormComponent
            isOpen={isOpenVisit}
            openPanel={openPanelConcept}
            dismissPanel={handleConceptClosePanel}
            ContractId={props.ContractId}
            ContractAddendumId={props.ContractAddendumId}
            ContractConceptId={selectedConcept?.id}
            CurrentConcepts={props.Contract?.concepts || []}
            CurrentStages={props.Addendum?.stages || []}
        />
    </>;

    const normalized = (v?: string) => (v || "").toString().toLowerCase();
    const term = normalized(filterText);
    const filteredConcepts = term
        ? props.ContractConcepts.filter(cc => {
            const n = normalized(cc.concept?.conceptName);
            const d = normalized(cc.concept?.conceptDescription);
            const r = normalized(cc.concept?.conceptReferenceId as any);
            return n.includes(term) || d.includes(term) || r.includes(term);
        })
        : props.ContractConcepts;

    const branchFilteredConcepts = selectedBranchIds.length === 0
        ? filteredConcepts
        : filteredConcepts.filter((cc) => {
            const conceptBranchIds = getConceptBranchIds(cc);
            if (conceptBranchIds.length === 0) {
                return false;
            }
            const branchIds = new Set(conceptBranchIds);
            return selectedBranchIds.some((id) => branchIds.has(id));
        });

    const conceptNameById = useMemo(() => {
        const lookup: Record<number, string> = {};
        (props.ContractConcepts || []).forEach((concept) => {
            if (typeof concept.id === 'number' && concept.id > 0) {
                const name = (concept.concept?.conceptName || '').trim();
                if (name.length > 0) {
                    lookup[concept.id] = name;
                }
            }
        });
        return lookup;
    }, [props.ContractConcepts]);

    return (
        <>
            {ComponentActions}
            <Separator />
            {ErrorMessage}
            {props.isLoading ? Loading : null}
            <div className="coi-list">
                {branchFilteredConcepts?.map((concept: ContractConcept, index) => (
                    <ConceptItemComponent
                        Concept={concept} index={index}
                        ConceptNameById={conceptNameById}
                        isCloning={cloningConceptId === concept.id}
                        onEdit={(currentConcept: ContractConcept) => HandleEditEvent(currentConcept)}
                        onDelete={(currentConcept: ContractConcept) => HandleDeleteEvent(currentConcept)}
                        onClone={(currentConcept: ContractConcept) => HandleCloneEvent(currentConcept)}
                        onToggleEnabled={(currentConcept: ContractConcept, enabled: boolean) =>
                            props.SetContractConceptEnabled(currentConcept.id, enabled, props.ContractId, props.ContractAddendumId)}
                        onToggleApproved={(currentConcept: ContractConcept, approved: boolean) =>
                            props.SetContractConceptApproved(currentConcept.id, approved, props.ContractId, props.ContractAddendumId)}
                        // La categoría viaja en el PUT del concepto; el nombre lo resuelve el servidor en la próxima lectura.
                        onChangeCategory={(currentConcept: ContractConcept, category) => {
                            setCategoryConceptId(currentConcept.id);
                            // Endpoint dedicado: escribe sólo la categoría, no reconstruye el concepto.
                            props.SetContractConceptCategory(currentConcept, category?.id ?? null);
                        }}
                        isChangingCategory={categoryConceptId === concept.id && props.isUpdatingConcept}
                        categoryNameOf={(categoryId: number) => props.CategoryNameById[categoryId]}
                        isDeleting={props.isDeleting}
                        failonDeleting={props.failOnDeleting}
                        error={props.error}
                    />
                ))}
            </div>
            {NoRegistries}
            {Forms}
        </>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ConceptListComponent as any);